import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, Space, Divider, Steps, Select, Checkbox, Alert } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  BankOutlined,
  PhoneOutlined,
  GlobalOutlined,
  BugOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { SERVICE_TITLE } from '@shared/config/service-config';
import { katakanaRule, phoneNumberRule } from '@/constants/validation';
import { PhoneInput, EmailInput } from '../components/common';
import { resolveCurrentClientType } from '../lib/clientType';
import { SEOHead } from '../components/SEOHead';

const isDevelopment = import.meta.env.DEV;

const { Title, Text } = Typography;

interface RegistrationForm {
  // Company information
  companyName: string;
  industry?: string;
  industryId?: string;

  // User information
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  nameKana?: string;
  phone?: string;

  // Terms agreement
  agreeToTerms: boolean;
}

export function RegisterPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const messageApi = message;
  const [, setSelectedIndustryId] = useState<string | undefined>();
  const [devUserCreating, setDevUserCreating] = useState(false);
  const [devCredentials, setDevCredentials] = useState<{
    email: string;
    password: string;
    billingEmail?: string;
    taxNumber?: string;
  } | null>(null);

  // 業界リスト取得
  const { data: industries, isLoading: industriesLoading } = trpc.industry.getAll.useQuery();

  // 開発環境用：ランダムユーザー作成mutation
  const createRandomUserMutation = trpc.devTools.createRandomUserAndLogin.useMutation({
    onSuccess: (data) => {
      if (data.tokens) {
        // 自動ログイン
        setAuth(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        messageApi.success(`デバッグユーザーを作成してログインしました: ${data.credentials.email}`);
        // 認証情報を表示
        setDevCredentials({
          ...data.credentials,
          billingEmail: data.billingInfo?.billingEmail,
          taxNumber: data.billingInfo?.taxNumber,
        });
        // 少し待ってからダッシュボードに遷移
        setTimeout(() => {
          navigate('/app/dashboard', { replace: true });
        }, 1500);
      }
      setDevUserCreating(false);
    },
    onError: (error) => {
      messageApi.error(`デバッグユーザー作成に失敗: ${error.message}`);
      setDevUserCreating(false);
    },
  });

  const handleCreateDevUser = async () => {
    setDevUserCreating(true);
    await createRandomUserMutation.mutateAsync();
  };

  const utils = trpc.useUtils();
  const registerMutation = trpc.auth.registerCompany.useMutation({
    onSuccess: (data) => {
      if (!data?.user) {
        messageApi.error('登録情報の取得に失敗しました。もう一度お試しください。');
        setLoading(false);
        return;
      }
      // メール認証が必要な場合（本番環境）
      if (data.verificationRequired === true && data.verification) {
        messageApi.success('登録が完了しました！メールアドレスの認証を行ってください。');

        // セッションストレージにメール認証情報を保存
        const verificationData = {
          email: data.user.email,
          maskedEmail: data.verification.maskedEmail,
          expiresAt: data.verification.expiresAt,
          resendAvailableAt: data.verification.resendAvailableAt,
        };

        try {
          window.sessionStorage.setItem('pending-login-verification', JSON.stringify(verificationData));
        } catch (e) {
          console.error('Failed to save verification data:', e);
        }

        // ログインページに遷移（メール認証画面が自動表示される）
        navigate('/login', { replace: true });
        setLoading(false);
        return;
      }

      // メール認証が不要な場合（開発環境）
      if ('tokens' in data && data.tokens) {
        setAuth(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        messageApi.success('会社登録が完了しました！フリープランでご利用を開始します。');
        navigate('/app/dashboard', { replace: true });
      } else {
        messageApi.error('登録は成功しましたが、認証情報の取得に失敗しました。ログインページからログインしてください。');
        navigate('/login', { replace: true });
        setLoading(false);
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '登録に失敗しました';
      messageApi.error(errorMessage);
      setLoading(false);
    },
  });

  const onFinish = async (values: RegistrationForm) => {
    setLoading(true);

    // Remove confirmPassword and agreeToTerms before sending to API
    const { confirmPassword, agreeToTerms, ...registrationData } = values;
    void confirmPassword;
    void agreeToTerms;

    try {
      await registerMutation.mutateAsync({
        ...registrationData,
        clientType: resolveCurrentClientType(),
      });
    } catch (error: unknown) {
      console.error('Registration error:', error);
      setLoading(false);
    }
  };

  const validateEmail = async (_: unknown, value: string) => {
    if (!value) return Promise.resolve();

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error('有効なメールアドレスを入力してください'));
    }

    try {
      const result = await utils.auth.checkAvailability.fetch({
        email: value,
        clientType: resolveCurrentClientType(),
      });
      if (result && !result.emailAvailable) {
        return Promise.reject(new Error('このメールアドレスは既に登録されています'));
      }
    } catch (error) {
      console.warn('Email availability check failed:', error);
      // API error, allow validation to pass to prevent blocking users
    }

    return Promise.resolve();
  };

  const validatePassword = (_: unknown, value: string) => {
    if (!value) return Promise.resolve();

    if (value.length < 8) {
      return Promise.reject(new Error('パスワードは8文字以上必要です'));
    }

    if (value.length > 255) {
      return Promise.reject(new Error('パスワードは255文字以下で入力してください'));
    }

    // Check for at least one uppercase, one lowercase, and one number
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);

    if (!hasUppercase || !hasLowercase || !hasNumber) {
      return Promise.reject(new Error('パスワードは大文字、小文字、数字を含む必要があります'));
    }

    return Promise.resolve();
  };

  const nextStep = () => {
    form.validateFields(['companyName', 'industryId'])
      .then(() => {
        setCurrentStep(1);
      })
      .catch(() => {
        messageApi.error('会社情報を正しく入力してください');
      });
  };

  const prevStep = () => {
    setCurrentStep(0);
  };

  const steps = [
    {
      title: '会社情報',
      description: '会社の基本情報を入力',
    },
    {
      title: '管理者情報',
      description: '管理者アカウントを作成',
    },
  ];

  return (
    <>
      <SEOHead
        title="無料登録でサービス内容を確認｜一元帳"
        description="設備・部品・業者・作業履歴を一元管理できる設備管理ソフト「一元帳」。無料登録でサービス内容や機能、画面イメージを確認でき、導入前の検討に役立ちます。"
      />
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}>
        <Card style={{
          width: '100%',
          maxWidth: 600,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Title level={2} style={{ marginBottom: 8, color: '#1890ff', cursor: 'pointer' }}>
                  {SERVICE_TITLE}
                </Title>
              </Link>
              <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
                新規登録
              </Title>
              <Text type="secondary">フリープランでご利用を開始</Text>
            </div>

            <Steps current={currentStep} items={steps} />

            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              autoComplete="off"
              layout="vertical"
              validateTrigger="onBlur"
            >
              {/* Hidden fields for step 0 when on step 1 */}
              <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Title level={4}>会社情報</Title>

                  <Form.Item
                    name="companyName"
                    label="会社名"
                    rules={[
                      { required: true, message: '会社名を入力してください' },
                      { max: 200, message: '会社名は200文字以下で入力してください' }
                    ]}
                  >
                    <Input
                      prefix={<BankOutlined />}
                      placeholder="株式会社〇〇"
                      size="large"
                    />
                  </Form.Item>



                  <Form.Item
                    name="industryId"
                    label="業界"
                    rules={[
                      { required: true, message: '業界を選択してください' }
                    ]}
                  >
                    <Select
                      placeholder="業界を選択"
                      size="large"
                      loading={industriesLoading}
                      suffixIcon={<GlobalOutlined />}
                      onChange={(value) => {
                        setSelectedIndustryId(value);
                      }}
                    >
                      {industries?.map((industry) => (
                        <Select.Option key={industry.id} value={industry.id}>
                          {industry.name}
                          {industry.nameEn && ` (${industry.nameEn})`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>


                  <Form.Item>
                    <Button
                      type="primary"
                      onClick={nextStep}
                      size="large"
                      block
                    >
                      次へ
                    </Button>
                  </Form.Item>
                </Space>
              </div>

              <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                {/* Hidden fields for step 0 when on step 1 to preserve values */}
                <Form.Item name="companyName" style={{ display: 'none' }}>
                  <Input />
                </Form.Item>
                <Form.Item name="industryId" style={{ display: 'none' }}>
                  <Input />
                </Form.Item>

                {/* Step 1 visible content */}
                {currentStep === 1 && (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4}>管理者アカウント</Title>

                    <Form.Item
                      name="email"
                      label="メールアドレス"
                      rules={[
                        { required: true, message: 'メールアドレスを入力してください' },
                        { validator: validateEmail }
                      ]}
                    >
                      <EmailInput
                        type="email"
                        name="email"
                        autoComplete="username"
                        prefix={<MailOutlined />}
                        placeholder="admin@example.com"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="name"
                      label="氏名"
                      rules={[
                        { required: true, message: '氏名を入力してください' },
                        { max: 100, message: '氏名は100文字以下で入力してください' }
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="山田 太郎"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="nameKana"
                      label="氏名（カナ）"
                      rules={[
                        { required: true, message: '氏名（カナ）を入力してください' },
                        { max: 100, message: '100文字以内で入力してください' },
                        katakanaRule(),
                      ]}
                    >
                      <Input
                        placeholder="ヤマダ タロウ"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="phone"
                      label="電話番号"
                      rules={[
                        { required: true, message: '電話番号を入力してください' },
                        { max: 20, message: '電話番号は20文字以内で入力してください' },
                        phoneNumberRule(),
                      ]}
                    >
                      <PhoneInput
                        prefix={<PhoneOutlined />}
                        placeholder="03-1234-5678"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="パスワード"
                      rules={[
                        { required: true, message: 'パスワードを入力してください' },
                        { validator: validatePassword }
                      ]}
                      extra="8文字以上で、大文字、小文字、数字を含む必要があります"
                    >
                      <Input.Password
                        name="password"
                        id="password"
                        autoComplete="new-password"
                        prefix={<LockOutlined />}
                        placeholder="8文字以上のパスワード"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="パスワード（確認）"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: 'パスワード確認を入力してください' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('パスワードが一致しません'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        name="confirm_password"
                        id="confirm_password"
                        autoComplete="new-password"
                        prefix={<LockOutlined />}
                        placeholder="パスワードを再入力"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="agreeToTerms"
                      valuePropName="checked"
                      rules={[
                        {
                          validator: (_, value) =>
                            value
                              ? Promise.resolve()
                              : Promise.reject(new Error('登録を完了するには、利用規約と個人情報保護方針および個人情報の取り扱いについてに同意する必要があります')),
                        },
                      ]}
                      style={{ marginBottom: 24 }}
                    >
                      <div style={{
                        padding: 16,
                        background: '#fafafa',
                        border: '1px solid #d9d9d9',
                        borderRadius: 8,
                      }}>
                        <Checkbox
                          aria-label="利用規約と個人情報保護方針および個人情報の取り扱いについてへの同意"
                          style={{ fontSize: 14, lineHeight: 1.8 }}
                        >
                          <span style={{ marginLeft: 4 }}>
                            <Link to="/legal/terms-privacy" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                              利用規約
                            </Link>
                            <span style={{ margin: '0 4px' }}>と</span>
                            <a href="https://dio-meta.com/privacypolicy/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                              個人情報保護方針および個人情報の取り扱いについて
                            </a>
                            <span style={{ marginLeft: 4 }}>を確認し同意する</span>
                          </span>
                        </Checkbox>
                      </div>
                    </Form.Item>

                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        size="large"
                        style={{ width: '100%', height: 56, fontSize: 18, fontWeight: 600 }}
                      >
                        登録完了
                      </Button>
                      <Button
                        onClick={prevStep}
                        type="text"
                        size="small"
                        style={{ color: '#666' }}
                      >
                        ← 前のページに戻る
                      </Button>
                    </div>
                  </Space>
                )}
              </div>
            </Form>

            <Divider />

            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">
                既にアカウントをお持ちですか？{' '}
                <Link to="/login">
                  <Button type="link" style={{ padding: 0 }}>
                    ログイン
                  </Button>
                </Link>
              </Text>
            </div>

            <div style={{
              padding: 16,
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
            }}>
              <Title level={5} style={{ color: '#52c41a', marginBottom: 8 }}>
                フリープランに含まれるもの：
              </Title>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#389e0d' }}>
                <li>設備登録: 10台まで</li>
                <li>ダッシュボード・保全カレンダー</li>
                <li>作業指示書・基本レポート</li>
              </ul>
            </div>

            {/* 開発環境専用：ランダムユーザー作成ボタン */}
            {isDevelopment && (
              <div style={{
                padding: 16,
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
              }}>
                <Title level={5} style={{ color: '#fa8c16', marginBottom: 8 }}>
                  <BugOutlined /> 開発環境専用
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  ランダムな会社・ユーザーを作成して自動ログインします
                </Text>
                <Button
                  type="primary"
                  danger
                  icon={<BugOutlined />}
                  onClick={handleCreateDevUser}
                  loading={devUserCreating}
                  style={{ width: '100%' }}
                >
                  デバッグユーザーを作成してログイン
                </Button>
                {devCredentials && (
                  <Alert
                    type="info"
                    style={{ marginTop: 12 }}
                    message="作成された認証情報"
                    description={
                      <div>
                        <div><strong>Email:</strong> {devCredentials.email}</div>
                        <div><strong>Password:</strong> {devCredentials.password}</div>
                        {devCredentials.billingEmail && (
                          <div><strong>請求先メール:</strong> {devCredentials.billingEmail}</div>
                        )}
                        {devCredentials.taxNumber && (
                          <div><strong>法人番号:</strong> {devCredentials.taxNumber}</div>
                        )}
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </Space>
        </Card>
      </div>
    </>
  );
}



